import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useWidgetContext } from '@widget/core';
import { useRerender, useLoading } from '@toolbox/hooks';
import { fetchFormattedValuation } from './fetchFormattedValuation';
import { ValuationContextProvider } from './ValuationContext';

import type { ReactFCWithChildren } from '@widget/types/ReactFCWithChildren';

type ValuationProviderProps = {
  onValuationChange?: (valuation: string | null) => void;
};

export const ValuationProvider: ReactFCWithChildren<ValuationProviderProps> = ({
  children,
  onValuationChange,
}): JSX.Element => {
  const { getFormData } = useWidgetContext();
  const [valuation, setValuation] = useState<string | null>(null);
  const [loading, handleLoading] = useLoading(false);
  const consumersRef = useRef<Record<string, boolean>>({});
  const rerender = useRerender();

  const registerConsumer = (id: string): void => {
    consumersRef.current[id] = true;
    // after update of the consumers we should rerender component to trigger necessary hooks
    rerender();
  };
  const unregisterConsumer = (id: string): void => {
    delete consumersRef.current[id];
    // after update of the consumers we should rerender component to trigger necessary hooks
    rerender();
  };
  const hasConsumers = (): boolean => !!Object.keys(consumersRef.current).length;

  useEffect(() => {
    if (hasConsumers()) {
      (async () => {
        const response = await handleLoading(() => fetchFormattedValuation(getFormData()));
        const valuation = response || null;
        setValuation(valuation);
        onValuationChange?.(valuation);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getFormData, hasConsumers()]);

  const context = useMemo(
    () => ({
      registerConsumer,
      unregisterConsumer,
      valuation,
      loading,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [valuation, loading],
  );

  return <ValuationContextProvider value={context}>{children}</ValuationContextProvider>;
};
